// SITUAÇÃO DA ESTAÇÃO — o que a gestão precisa saber antes de abrir o detalhe.
//
// Módulo PURO (nada de React, nada de antd): é o que permite testar a regra sem
// montar tela, e é o que impede que a mesma conta apareça diferente no card, na
// tabela, no KPI e no pino do mapa.
//
// A detecção de SILÊNCIO é portada de `monitoramento-frontend/src/silencio.ts`
// (portal do cliente), mesmo movimento do `utils/escalaGrafico.ts`. Duas
// diferenças deliberadas, ambas porque o público aqui é interno:
//
//  1. O portal ESCONDE o valor da estação muda — lá o risco é o cliente ler um
//     número de 3 dias atrás como se fosse o nível de agora. Aqui é o oposto:
//     ver o último valor é como se distingue sensor TRAVADO (nível parado no
//     mesmo número) de sensor MUDO (parou de mandar).
//  2. A cadência do portal é a mediana dos intervalos da variável em destaque;
//     a nossa é a mediana entre TRANSMISSÕES (instantes distintos), calculada no
//     SQL do `estacaoListar`. Para estação de 1 variável dá o mesmo número; em
//     estação multi-variável a nossa é a mais fiel ao que o modem faz. A REGRA
//     de silêncio (3× a cadência, piso de 1 h) é a mesma nos dois — é ela que
//     não pode divergir, senão o ERP e o portal discordam sobre a mesma estação.
import type { Estacao } from './shared';

// ── Silêncio ────────────────────────────────────────────────────────────────
export type Silencio =
  | { silenciosa: false }
  | { silenciosa: true; desde: string; cadenciaSeg: number };

export function ehTelemetrica(e: Estacao): boolean {
  return (e.formas || []).includes('telemetrica');
}

export function avaliarSilencio(e: Estacao, agoraMs = Date.now()): Silencio {
  if (!ehTelemetrica(e) || !e.cadencia_seg) return { silenciosa: false };
  const ult = e.ultima_telemetria_em;
  if (!ult) return { silenciosa: false };   // nunca transmitiu ≠ silenciosa
  // 3× absorve uma falha de envio isolada + atraso de rede; o piso de 1 h evita
  // alarme nervoso em modem de bancada configurado a cada 60 s.
  const limiarMs = Math.max(3 * e.cadencia_seg, 3600) * 1000;
  if (agoraMs - +new Date(ult) <= limiarMs) return { silenciosa: false };
  return { silenciosa: true, desde: ult, cadenciaSeg: e.cadencia_seg };
}

export function textoCadencia(seg: number | null | undefined): string {
  if (!seg) return '—';
  if (seg < 90) return `${Math.round(seg)} s`;
  if (seg < 5400) return `${Math.round(seg / 60)} min`;
  return `${(seg / 3600).toFixed(seg % 3600 ? 1 : 0)} h`;
}

// "há 6 h", "há 41 d" — é a IDADE que dispara a ação, não o carimbo. Um
// "07/08 00:52" obriga a pessoa a fazer a conta de cabeça; "há 13 h", não.
export function idadeTexto(iso: string | null | undefined, agoraMs = Date.now()): string {
  if (!iso) return 'nunca';
  const min = (agoraMs - +new Date(iso)) / 60000;
  if (!Number.isFinite(min)) return 'nunca';
  if (min < 2) return 'agora';
  if (min < 60) return `há ${Math.round(min)} min`;
  if (min < 48 * 60) return `há ${Math.round(min / 60)} h`;
  const d = Math.round(min / 1440);
  if (d < 60) return `há ${d} d`;
  return `há ${Math.round(d / 30)} meses`;
}

// ── Situação: UMA classificação, usada pelo card, pela tabela, pelo KPI e
//    pelo pino do mapa. Ordem = gravidade.
export type Estado = 'sem_dado' | 'silenciosa' | 'modem_fora' | 'ok' | 'inativa';

export const ESTADO_ROTULO: Record<Estado, string> = {
  sem_dado: 'nunca mediu',
  silenciosa: 'sem transmitir',
  modem_fora: 'modem fora',
  ok: 'em dia',
  inativa: 'inativa',
};

// Papéis, não decoração — as mesmas cores do portal, pra quem olha os dois não
// ter que reaprender: âmbar = atenção, cinza = sem dado, verde = fluindo.
export const ESTADO_COR: Record<Estado, string> = {
  sem_dado: '#9AA1B8',
  silenciosa: '#B07500',
  modem_fora: '#B07500',
  ok: '#0a7f4a',
  inativa: '#c0c4cc',
};

export function estadoDe(e: Estacao, agoraMs = Date.now()): Estado {
  // Inativa vem primeiro: estação desligada de propósito não é problema, e
  // marcá-la como "sem transmitir" encheria o painel de alarme falso. As 4 com
  // maior acervo histórico estão inativas.
  if (e.status === 'inativa') return 'inativa';
  if (!e.ultimo_dado_em) return 'sem_dado';
  if (avaliarSilencio(e, agoraMs).silenciosa) return 'silenciosa';
  if (e.modem_conectado === false) return 'modem_fora';
  return 'ok';
}

// Só o que FALTA, e só onde faz sentido faltar. Card sem pendência não mostra
// rodapé nenhum — indicador que sempre marca cheio é decoração.
export interface Pendencia { chave: string; rotulo: string; dica: string }

export function pendenciasDe(e: Estacao): Pendencia[] {
  const p: Pendencia[] = [];
  const tipos = e.tipos?.length ? e.tipos : (e.tipo ? [e.tipo] : []);
  const ehFluvial = tipos.some(t => t === 'fluviometrica' || t === 'linimetrica');
  if (!e.tem_ficha) p.push({ chave: 'ficha', rotulo: 'sem ficha', dica: 'Ficha descritiva padrão ANA não preenchida' });
  if (!e.token_hash) p.push({ chave: 'qr', rotulo: 'sem QR', dica: 'Sem link público de leitura — não dá pra imprimir a placa' });
  // Cota zero só importa onde há régua: é o datum que converte leitura em cota.
  if (ehFluvial && (e.formas || []).includes('convencional') && e.cota_zero == null) {
    p.push({ chave: 'cota', rotulo: 'sem cota-zero', dica: 'Sem o datum da régua, a leitura não vira cota' });
  }
  // Campanha de vazão que não virou curva é serviço pago sem o produto final.
  if (ehFluvial && (e.medicoes_vazao || 0) > 0 && !e.tem_curva) {
    p.push({ chave: 'curva', rotulo: `${e.medicoes_vazao} medições sem curva`,
             dica: 'Já há medição de vazão em campo, mas nenhuma curva-chave ativa' });
  }
  return p;
}

// ── Contadores do painel ────────────────────────────────────────────────────
// Um número que sempre marca o mesmo valor não muda decisão nenhuma: por isso
// aqui NÃO tem "total de estações" nem "14 de 14 com coordenada". Todo contador
// desta lista aponta pra algo que alguém precisa resolver.
export interface Kpi {
  chave: string; titulo: string; valor: number; dica: string;
  cor: string; filtro: (e: Estacao) => boolean;
}

export function kpisDe(estacoes: Estacao[], agoraMs = Date.now()): Kpi[] {
  const fSilencio = (e: Estacao) => estadoDe(e, agoraMs) === 'silenciosa';
  const fSemDado = (e: Estacao) => estadoDe(e, agoraMs) === 'sem_dado';
  const fModem = (e: Estacao) => e.status !== 'inativa' && e.modem_conectado === false;
  const fCurva = (e: Estacao) => pendenciasDe(e).some(p => p.chave === 'curva');
  const fFicha = (e: Estacao) => e.status !== 'inativa' && !e.tem_ficha;
  return [
    { chave: 'silenciosa', titulo: 'Sem transmitir', cor: ESTADO_COR.silenciosa, filtro: fSilencio,
      valor: estacoes.filter(fSilencio).length,
      dica: 'Telemétrica ativa que passou de 3× a própria cadência sem mandar dado' },
    { chave: 'sem_dado', titulo: 'Nunca mediram', cor: ESTADO_COR.sem_dado, filtro: fSemDado,
      valor: estacoes.filter(fSemDado).length,
      dica: 'Estação ativa cadastrada que nunca recebeu leitura — nem régua, nem sensor' },
    { chave: 'modem', titulo: 'Modem fora', cor: ESTADO_COR.modem_fora, filtro: fModem,
      valor: estacoes.filter(fModem).length,
      dica: 'Desconectado do IoT Core agora (pode estar conectado e mudo — são coisas diferentes)' },
    { chave: 'curva', titulo: 'Medição sem curva', cor: '#7a5cff', filtro: fCurva,
      valor: estacoes.filter(fCurva).length,
      dica: 'Já houve campanha de vazão em campo, mas nenhuma curva-chave ativa — serviço feito sem o produto' },
    { chave: 'ficha', titulo: 'Sem ficha ANA', cor: '#0930D0', filtro: fFicha,
      valor: estacoes.filter(fFicha).length,
      dica: 'Ficha descritiva não preenchida — é o documento que acompanha a estação e o que o cliente baixa no portal' },
  ];
}
